import { AntTag, useHelmet } from '@agro-club/agroclub-shared'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { endpoints } from 'modules/endpoints'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { AButton } from 'views/components/Analytics'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import * as Layout from 'views/layouts/NewLayout'
import { ConfirmArchive } from 'views/pages/Bid/BidDetail/Bid/Samples/ConfirmArchive'
import { EditSampleModal } from 'views/pages/Bid/BidDetail/Bid/Samples/EditSampleModal'
import { WrapperButton } from 'views/pages/User/UserDetail/styled'
import * as Header from 'views/ui/Header/Header'
import SamplesRoutes from '../routes'
import { SampleDetailed, SamplePartsTab, SampleStatus } from '../types'
import { LeftBlock } from './LeftBlock/LeftBlock'
import { RightBlock } from './RightBlock/RightBlock'
import { statusColorMap } from '../helpers'
import { IconSample } from 'views/pages/Deal/Board/styled'
import { ConfirmCancel } from 'views/pages/Bid/BidDetail/Bid/Samples/ConfirmCancel'
import useFormatDate from 'hooks/useFormatDate'
import { ExportBotWrapper } from './ExportBotWrapper'
import { PublishToLocalWrapper } from './PublishToLocalWrapper'
import { SamplePause } from './SamplePause'

const Content = styled.div`
  margin-top: 16px;
`
const WrapperTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0 8px;
`

export const SampleDetail: FC = () => {
  const { id } = useParams()
  const { t } = useTranslation('samples')
  const [showModal, setShowModal] = useState(false)
  const [isArchiveModalVisible, setArchiveModalVisible] = useState(false)
  const [isCancelModalVisible, setCancelModalVisible] = useState(false)
  const [progressSample, sample, refetchSample] = useSingleEntity<SampleDetailed>(endpoints.samples(id))
  const [partsProgress, partsTable] = useSingleEntity<SamplePartsTab[]>(endpoints.samplePartsAsJson(id))
  useHelmet({ title: sample?.title || `Sample ${id}` })
  const formatDate = useFormatDate()

  if (!sample) return null

  return (
    <APageWrapper page="sampleDetail" place="SampleDetail" id={id}>
      <Layout.WrapperContent>
        <Layout.Header>
          <Layout.TopHeader>
            <Header.Breadcrumbs
              routes={[
                { path: SamplesRoutes.List, breadcrumbName: t('list.heading') },
                { breadcrumbName: sample.title },
              ]}
            />
            <WrapperButton>
              <PublishToLocalWrapper sample={sample} onSucess={refetchSample} />
              <ExportBotWrapper sample={sample} onSucess={refetchSample} />
              <AButton id="edit" color="secondary" size="medium" onClick={() => setShowModal(true)}>
                {t('common:edit')}
              </AButton>
              {sample.status === SampleStatus.published && (
                <>
                  <Header.LineSeparator />
                  <AButton
                    onClick={() => setArchiveModalVisible(true)}
                    analyticsPayload={{ id }}
                    color="destruct"
                    size="medium"
                    id="archive"
                  >
                    {t('common:archive')}
                  </AButton>
                </>
              )}
              <SamplePause sample={sample} refetch={refetchSample} />
              {sample.status === SampleStatus.in_progress && (
                <>
                  <Header.LineSeparator />
                  <AButton
                    onClick={() => setCancelModalVisible(true)}
                    analyticsPayload={{ id }}
                    color="destruct"
                    size="medium"
                    id="archive"
                  >
                    {t('cancelSample')}
                  </AButton>
                </>
              )}
            </WrapperButton>
          </Layout.TopHeader>
        </Layout.Header>

        <WrapperTitle>
          <Header.Title margin="0" compact title={sample.title}></Header.Title>
          <AntTag color={statusColorMap[sample.status]}>{t(`sampleStatus.${sample.status}`)}</AntTag>
          {sample.sample_request_status && (
            <AntTag icon={<IconSample />} color="blue">
              {t(`sampleRequestStatuses.${sample.sample_request_status}`)}
            </AntTag>
          )}
          {sample.pause_reason && (
            <AntTag color="orange">{`${t('sampleReaction.paused')}: ${sample.pause_reason}`}</AntTag>
          )}
          {sample.sent_to_export_bot_date && (
            <AntTag color="magenta">{`${t('sentToExportBot')}: ${formatDate(sample.sent_to_export_bot_date)}`}</AntTag>
          )}
          {sample.sent_to_local_bot_date && (
            <AntTag color="cyan">{`${t('sentToLocalBot')}: ${formatDate(sample.sent_to_local_bot_date)}`}</AntTag>
          )}
        </WrapperTitle>

        <Content>
          <Layout.ContentTwoColumns>
            <LeftBlock sample={sample} partsProgress={partsProgress} partsTable={partsTable} />
            <RightBlock
              sampleProgress={progressSample}
              sampleRefetch={refetchSample}
              files={sample.files}
              sample={sample}
            />
          </Layout.ContentTwoColumns>
        </Content>
      </Layout.WrapperContent>
      {showModal && <EditSampleModal sample={sample} onClose={() => setShowModal(false)} refetch={refetchSample} />}
      {isArchiveModalVisible && (
        <ConfirmArchive onClose={() => setArchiveModalVisible(false)} sampleId={sample.id} refetch={refetchSample} />
      )}
      {isCancelModalVisible && (
        <ConfirmCancel onClose={() => setCancelModalVisible(false)} sampleId={sample.id} refetch={refetchSample} />
      )}
    </APageWrapper>
  )
}
